import { useAppSelector } from "app/hooks";
import { useAuth } from "hooks/auth";
import { InfiniteResponse } from "hooks/useManageInfiniteScroll";
import { querystring } from "models/Listing";
import { selectCurrentStoreData } from "reduxState/currentStoreData/currentStoreDataSlice";
import useSWR, { SWRResponse } from "swr";
import useSWRInfinite from "swr/infinite";
import { VisibleCampaign } from "types/campaigns";
import { MemberLeaderboard } from "types/leaderboards/ranking";
import { ListingProps, ListResponse } from "types/listing";
import {
  BackendMemberModel,
  MemberAchievementProgress,
  MemberAnalytics,
  MemberBadge,
  MemberCampaignsStatus,
  MemberHistory,
  MemberPayload,
  MemberReward,
  MembersStatus,
  MemberTierSet,
  ReferredMember,
} from "types/members";
import { MemberWalletListItem } from "types/wallets";
import { getEnvVariable } from "utilities/env";

export function useMember(memberID?: string) {
  const { code } = useAppSelector(selectCurrentStoreData);
  const { fetcher } = useAuth();

  const useMembersList = (
    props?: ListingProps,
  ): SWRResponse<ListResponse<BackendMemberModel>> => {
    return useSWR(
      () =>
        code
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${code}/member${querystring(props)}`
          : null,
      fetcher,
    );
  };
  const useInfiniteMembersList = (props?: ListingProps) => {
    return useSWRInfinite<InfiniteResponse<BackendMemberModel>>(
      (index) =>
        code
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${code}/member${querystring({ ...props, page: index + 1 })}`
          : null,
      fetcher,
      { revalidateFirstPage: false },
    );
  };
  const useMembersData = (): SWRResponse<BackendMemberModel> => {
    return useSWR(
      () =>
        code && memberID
          ? `${getEnvVariable("REACT_APP_API_URL")}/${code}/member/${memberID}`
          : null,
      fetcher,
    );
  };
  const useMembersStatus = (): SWRResponse<MembersStatus> => {
    return useSWR(
      () =>
        code && memberID
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${code}/member/${memberID}/status`
          : null,
      fetcher,
    );
  };
  const useMembersHistory = (
    props?: ListingProps,
  ): SWRResponse<ListResponse<MemberHistory>> => {
    return useSWR(
      () =>
        code && memberID
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${code}/member/${memberID}/history${querystring(props)}`
          : null,
      fetcher,
    );
  };
  const useMemberAvailableRewards = (
    props?: ListingProps,
  ): SWRResponse<ListResponse<MemberReward>> => {
    return useSWR(
      () =>
        code && memberID
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${code}/member/${memberID}/reward${querystring(props)}`
          : null,
      fetcher,
    );
  };
  const useMembersAchievements = (
    props?: ListingProps,
  ): SWRResponse<ListResponse<MemberAchievementProgress>> => {
    return useSWR(
      () =>
        code && memberID
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${code}/member/${memberID}/achievement${querystring(props)}`
          : null,
      fetcher,
    );
  };
  const useMembersCampaigns = (
    props?: ListingProps,
  ): SWRResponse<ListResponse<MemberCampaignsStatus>> => {
    return useSWR(
      () =>
        code && memberID
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${code}/member/${memberID}/campaign${querystring(props)}`
          : null,
      fetcher,
    );
  };
  const useMemberTierSet = (
    tierSetId: string,
    props?: ListingProps,
  ): SWRResponse<MemberTierSet, any> => {
    return useSWR(
      () =>
        code && memberID && tierSetId
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${code}/member/${memberID}/tierSet/${tierSetId}${querystring(
              props,
            )}`
          : null,
      fetcher,
    );
  };
  const useMembersVisibleCampaigns = (
    props?: ListingProps,
  ): SWRResponse<ListResponse<VisibleCampaign>> => {
    return useSWR(
      () =>
        code && memberID
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${code}/member/${memberID}/campaign/visible${querystring(props)}`
          : null,
      fetcher,
    );
  };
  const useReferredMembers = (
    props?: ListingProps,
  ): SWRResponse<ListResponse<ReferredMember>> => {
    return useSWR(
      () =>
        code
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${code}/referral${querystring(props)}`
          : null,
      fetcher,
    );
  };
  const addMember = async ({
    referralCustomerEmail,
    ...values
  }: MemberPayload): Promise<{ data: { customerId: string } }> => {
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/${code}/member`,
      {
        body: JSON.stringify({
          customer: {
            ...values,
            referral_customer_email: referralCustomerEmail,
          },
        }),
        headers: { "Content-Type": "application/json" },
        method: "post",
      },
    );
    const data = await response.json();
    if (response.ok) {
      return { data };
    } else {
      throw data;
    }
  };
  const editMember = async (
    memberId: string,
    { referralCustomerEmail, ...values }: MemberPayload,
  ): Promise<{ data: { customerId: string } }> => {
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/${code}/member/${memberId}`,
      {
        body: JSON.stringify({
          customer: {
            ...values,
            referral_customer_email: referralCustomerEmail,
          },
        }),
        headers: { "Content-Type": "application/json" },
        method: "put",
      },
    );
    const data = await response.json();
    if (response.ok) {
      return { data };
    } else {
      throw data;
    }
  };
  const activateMember = async (memberId: string) => {
    const response = await fetcher(
      `${getEnvVariable(
        "REACT_APP_API_URL",
      )}/${code}/member/${memberId}/activate`,
      { method: "post" },
    );
    if (response.status === 204) {
      return true;
    } else {
      throw await response.json();
    }
  };
  const deactivateMember = async (memberId: string) => {
    const response = await fetcher(
      `${getEnvVariable(
        "REACT_APP_API_URL",
      )}/${code}/member/${memberId}/deactivate`,
      { method: "post" },
    );
    if (response.status === 204) {
      return true;
    } else {
      throw await response.json();
    }
  };
  const anonymizeMember = async (memberId: string) => {
    const response = await fetcher(
      `${getEnvVariable(
        "REACT_APP_API_URL",
      )}/${code}/member/${memberId}/anonymize`,
      { method: "post" },
    );
    if (response.status === 204) {
      return true;
    } else {
      throw await response.json();
    }
  };
  const removeMember = async (memberId: string) => {
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/${code}/member/${memberId}`,
      { method: "delete" },
    );
    if (response.status === 204) {
      return true;
    } else {
      throw await response.json();
    }
  };
  const removeManuallyAssignedTier = async (
    memberID: string,
    tierSetId?: string,
  ) => {
    const response = await fetcher(
      `${getEnvVariable(
        "REACT_APP_API_URL",
      )}/${code}/member/${memberID}/remove-manually-level`,
      {
        method: "post",
        headers: { "Content-Type": "application/json" },
        body: tierSetId
          ? JSON.stringify({
              tierSetId: tierSetId,
            })
          : undefined,
      },
    );
    if (response.status === 204) {
      return true;
    } else {
      throw await response.json();
    }
  };

  const useMemberAnalytics = (
    withTransaction?: boolean,
  ): SWRResponse<MemberAnalytics> => {
    return useSWR(
      () =>
        code
          ? `${getEnvVariable("REACT_APP_API_URL")}/${code}/analytics/members${
              withTransaction === undefined
                ? ""
                : `?withTransaction=${withTransaction ? 1 : 0}`
            }`
          : null,
      fetcher,
    );
  };

  const redeemAssignedCoupon = async (memberID: string, couponID: string) => {
    const response = await fetcher(
      `${getEnvVariable(
        "REACT_APP_API_URL",
      )}/${code}/member/${memberID}/reward/redeem`,
      {
        body: JSON.stringify({ couponCode: couponID }),
        headers: { "Content-Type": "application/json" },
        method: "post",
      },
    );
    if (response.status === 200) {
      return true;
    } else {
      throw await response.json();
    }
  };

  const reissueAssignedCoupon = async (memberID: string, couponID: string) => {
    const response = await fetcher(
      `${getEnvVariable(
        "REACT_APP_API_URL",
      )}/${code}/member/${memberID}/reward/reissue`,
      {
        body: JSON.stringify({ couponCode: couponID }),
        headers: { "Content-Type": "application/json" },
        method: "post",
      },
    );
    if (response.status === 200) {
      return true;
    } else {
      throw await response.json();
    }
  };

  const useMemberWalletsData = (
    props?: ListingProps,
  ): SWRResponse<ListResponse<MemberWalletListItem>> => {
    return useSWR(
      () =>
        code && memberID
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${code}/member/${memberID}/wallet${querystring(props)}`
          : null,
      fetcher,
    );
  };

  const assignMemberToTier = async (memberID: string, tierID: string) => {
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/${code}/member/${memberID}/tier`,
      {
        body: JSON.stringify({ levelId: tierID }),
        headers: { "Content-Type": "application/json" },
        method: "post",
      },
    );
    if (response.status === 204) {
      return true;
    } else {
      throw await response.json();
    }
  };

  const changeMemberPassword = async (memberID: string, password: string) => {
    const response = await fetcher(
      `${getEnvVariable(
        "REACT_APP_API_URL",
      )}/${code}/member/${memberID}/password`,
      {
        body: JSON.stringify({ plainPassword: password }),
        headers: { "Content-Type": "application/json" },
        method: "post",
      },
    );
    if (response.status === 204) {
      return true;
    } else {
      throw await response.json();
    }
  };

  const useMemberBadges = (
    props?: ListingProps,
  ): SWRResponse<ListResponse<MemberBadge>> => {
    return useSWR(
      () =>
        code && memberID
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${code}/member/${memberID}/badge${querystring(props)}`
          : null,
      fetcher,
    );
  };

  const updateMemberBadgeCompletionCount = async ({
    badgeId,
    completedCount,
  }: {
    badgeId: string;
    completedCount?: number;
  }) => {
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/${code}/member/${memberID}/badge/${badgeId}`,
      {
        body: JSON.stringify({
          badge: {
            completedCount: completedCount,
          },
        }),
        headers: { "Content-Type": "application/json" },
        method: "PUT",
      },
    );
    if (response.status === 204) {
      return true;
    } else {
      throw await response.json();
    }
  };

  const useMemberLeaderboard = (
    leaderboardId: string,
    memberId: string,
    props?: ListingProps,
  ): SWRResponse<MemberLeaderboard> => {
    return useSWR(
      () =>
        code && leaderboardId && memberId
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${code}/campaign/${leaderboardId}/leaderboard/member/${memberId}${querystring(props)}`
          : null,
      fetcher,
    );
  };

  return {
    activateMember,
    addMember,
    anonymizeMember,
    assignMemberToTier,
    changeMemberPassword,
    deactivateMember,
    editMember,
    redeemAssignedCoupon,
    reissueAssignedCoupon,
    removeManuallyAssignedTier,
    removeMember,
    useMemberAvailableRewards,
    useInfiniteMembersList,
    useMemberAnalytics,
    useMembersAchievements,
    useMembersCampaigns,
    useMembersData,
    useMembersHistory,
    useMembersList,
    useMembersStatus,
    useMembersVisibleCampaigns,
    useMemberTierSet,
    useMemberWalletsData,
    useReferredMembers,
    useMemberBadges,
    updateMemberBadgeCompletionCount,
    useMemberLeaderboard,
  };
}
